import type {
  BulkCancelCreditChoice,
  BulkCancelPreview,
  CancellationCreditResolution,
  PaidCancellationAction,
} from "./staff.js";

export type CancellationChoices = Record<
  number,
  CancellationCreditResolution | PaidCancellationAction
>;
type Item = BulkCancelPreview["items"][number];

export function cancellationDecisionRequired(item: Item): boolean {
  return (
    item.can_cancel &&
    (item.entitlement?.decision_required ?? item.credit_resolution_required)
  );
}

function allowedActions(
  item: Item,
): Array<CancellationCreditResolution | PaidCancellationAction> {
  return (
    item.entitlement?.available_actions ?? item.available_credit_resolutions
  );
}

export function keepValidCreditChoices(
  preview: BulkCancelPreview,
  current: CancellationChoices,
): CancellationChoices {
  const next: CancellationChoices = {};
  for (const item of preview.items) {
    const choice = current[item.appointment_id];
    if (
      cancellationDecisionRequired(item) &&
      choice !== undefined &&
      allowedActions(item).includes(choice)
    )
      next[item.appointment_id] = choice;
  }
  return next;
}

export function cancellationChoicesComplete(
  preview: BulkCancelPreview,
  choices: CancellationChoices,
): boolean {
  return preview.items.filter(cancellationDecisionRequired).every((item) => {
    const choice = choices[item.appointment_id];
    return choice !== undefined && allowedActions(item).includes(choice);
  });
}

export function cancellationCreditChoices(
  preview: BulkCancelPreview,
  choices: CancellationChoices,
): BulkCancelCreditChoice[] {
  if (!cancellationChoicesComplete(preview, choices))
    throw new Error("Choose the credit or payment treatment for each lesson.");
  return preview.items
    .filter(cancellationDecisionRequired)
    .map((item) =>
      item.entitlement?.kind === "paid_cancellation"
        ? {
            appointment_id: item.appointment_id,
            paid_cancellation_action: choices[
              item.appointment_id
            ] as PaidCancellationAction,
          }
        : {
            appointment_id: item.appointment_id,
            credit_resolution: choices[
              item.appointment_id
            ] as CancellationCreditResolution,
          },
    );
}

export function chooseAllCancellationCredits(
  preview: BulkCancelPreview,
  current: CancellationChoices,
  choice: CancellationCreditResolution,
): CancellationChoices {
  const next = { ...current };
  for (const item of preview.items) {
    if (
      cancellationDecisionRequired(item) &&
      item.entitlement?.kind !== "paid_cancellation" &&
      allowedActions(item).includes(choice)
    )
      next[item.appointment_id] = choice;
  }
  return next;
}
